import React, { useEffect, useMemo, useState } from 'react';

import { MultiSelect } from 'primereact/multiselect';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FILTER_TIMELAPSE_TIME,
  VISUALIZATION_OPTIONS,
  VisualizationFormState,
  VisualizationType,
  matchesGrouping,
  normalize,
  parseQualifiedColumn,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { OptionType } from 'interfaces/shared.interface';
import { SelectFormInput, Input } from 'shared-components';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';

type Props = {
  control: any;
  selectedTable: string | null;
  selectedColumnsData: ReportingColumn[];
  onChange: (data: VisualizationFormState | null) => void;
  groupingColumn?: any;
  aggregateColumns?: { label: string; value: string; rawColumn?: string }[];
  initialVisualization?: VisualizationFormState | null;
  selectedVisualizationType?: VisualizationType | null;
  selectedVisualizationColumn?: string;
  selectedVisualizationColumns?: string[];
  selectedTimelapseType?: number;
  selectedVisualizationRowLimit?: number;
};

const CreateReportTemplateVisualizationBlock: React.FC<Props> = ({
  control,
  selectedTable,
  selectedColumnsData,
  onChange,
  groupingColumn,
  aggregateColumns,
  selectedVisualizationType,
  selectedVisualizationColumn,
  selectedVisualizationColumns,
  selectedTimelapseType,
  selectedVisualizationRowLimit,
  initialVisualization,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<VisualizationType | null>(null);
  const { setValue } = useFormContext();
  const [selectedColumnMeta, setSelectedColumnMeta] = useState<ReportingColumn | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [rowLimit, setRowLimit] = useState<number | undefined>(selectedVisualizationRowLimit);
  const [timelapseType, setTimelapseType] = useState<number>(2);
  const [isInitialized, setIsInitialized] = useState(false);

  const DEFAULT_TIME_TYPE = FILTER_TIMELAPSE_TIME.find((opt) => opt.value === 2) ?? { value: 2, label: 'Monthly' };

  const availableColumns: OptionType[] = useMemo(() => {
    const groupOptions = Array.isArray(groupingColumn)
      ? groupingColumn.map((col) => ({ label: col, value: col }))
      : groupingColumn
        ? [{ label: groupingColumn, value: groupingColumn }]
        : [];

    const aggOptions = aggregateColumns ?? [];

    if (groupOptions.length || aggOptions.length) {
      return [...groupOptions, ...aggOptions];
    }

    return selectedColumnsData
      .filter((col) => col.tableName === selectedTable)
      .map((col) => ({ label: col.name, value: col.name }));
  }, [groupingColumn, aggregateColumns, selectedColumnsData, selectedTable]);

  const handleTypeChange = (selected: OptionType | null) => {
    const selectedType = selected?.value ?? null;
    setType(selectedType);
    setColumns([]);
    setSelectedColumnMeta(null);
    setTimelapseType(2);

    if (!selectedType) {
      onChange(null);
      return;
    }

    if (selectedType === 'Table') {
      onChange({ type: 'Table', columns: [], rowLimit: 0 });
    } else {
      onChange({ type: selectedType, column: '' });
    }
  };

  const handleSingleColumnChange = (selected: OptionType | null) => {
    const val = selected?.value ?? '';
    const columnMeta = selectedColumnsData.find((col) => col.name === val && col.tableName === selectedTable) || null;

    if (type && type !== 'Table') {
      const updated: VisualizationFormState = {
        type,
        column: val,
        ...(columnMeta?.columnType === 5 ? { timelapseType } : {}),
      };
      onChange(updated);
    }

    setSelectedColumnMeta(columnMeta);
  };

  const handleTimelapseTypeChange = (selected: OptionType | null) => {
    const timelapseValue = selected?.value ? Number(selected.value) : DEFAULT_TIME_TYPE.value;
    setTimelapseType(timelapseValue);

    if (type && type !== 'Table' && selectedColumnMeta) {
      const updated: VisualizationFormState = {
        type,
        column: selectedColumnMeta.name,
        timelapseType: timelapseValue,
      };

      onChange(updated);
    }
  };

  const handleRowLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (raw === '') {
      setRowLimit(Number.NaN);
      if (type === 'Table') {
        onChange({ type: 'Table', columns, rowLimit: undefined });
      }
      return;
    }

    const value = Number(raw);
    if (!Number.isNaN(value)) {
      setRowLimit(value);
      if (type === 'Table') {
        onChange({ type: 'Table', columns, rowLimit: value });
      }
    }
  };

  const filteredGroupedOptions = useMemo(() => {
    const groups: Record<string, OptionType[]> = {};

    (selectedColumnsData || []).forEach((col) => {
      const key = col.tableName ?? 'Other';
      if (!groups[key]) {
        groups[key] = [];
      }
      const rawId = `${col.tableName}.${col.name}`;
      const value = normalize(rawId);
      groups[key].push({ label: col.displayName ?? col.name, value });
    });

    const hasGrouping = Array.isArray(groupingColumn) ? groupingColumn.length > 0 : !!groupingColumn;
    const hasAggregates = aggregateColumns && aggregateColumns.length > 0;

    if (hasGrouping || hasAggregates) {
      Object.keys(groups).forEach((k) => {
        if (k === selectedTable) {
          if (hasGrouping) {
            const gCols = Array.isArray(groupingColumn) ? groupingColumn : [groupingColumn];
            groups[k] = groups[k].filter((opt) => gCols.some((g: any) => matchesGrouping(opt.value, g)));
          } else {
            groups[k] = [];
          }
        }
      });
    }

    if (aggregateColumns?.length) {
      aggregateColumns.forEach(({ label, value, rawColumn }) => {
        let table: string;
        if (rawColumn) {
          const found = (selectedColumnsData || []).find((c) => c.name === rawColumn);
          table = found?.tableName ?? selectedTable ?? 'Aggregates';
        } else {
          const { table: parsed } = parseQualifiedColumn(value || '');
          table = parsed || 'Aggregates';
        }
        if (!groups[table]) {
          groups[table] = [];
        }
        groups[table].push({ label, value });
      });
    }

    const ordered: { label: string; items: OptionType[] }[] = [];
    if (selectedTable && groups[selectedTable]) {
      ordered.push({ label: selectedTable, items: groups[selectedTable] });
    }
    Object.keys(groups).forEach((table) => {
      if (table === selectedTable) {
        return;
      }
      ordered.push({ label: table, items: groups[table] });
    });

    ordered.forEach((g) => {
      const seen = new Set<string>();
      g.items = g.items.filter((it) => {
        const v = String(it.value);
        if (seen.has(v)) {
          return false;
        }
        seen.add(v);
        return true;
      });
    });

    return ordered;
  }, [selectedColumnsData, selectedTable, groupingColumn, aggregateColumns]);

  useEffect(() => {
    if (!isInitialized || type !== 'Table' || columns.length === 0) {
      return;
    }

    const validValues = new Set(filteredGroupedOptions.flatMap((g) => g.items.map((i) => String(i.value))));
    const filtered = columns.filter((c) => validValues.has(c));

    if (filtered.length !== columns.length) {
      setColumns(filtered);
      onChange({ type: 'Table', columns: filtered, rowLimit });
    }
  }, [filteredGroupedOptions]);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    const resolveColumnValue = (colVal: string) => {
      if (!colVal) {
        return colVal;
      }
      const normalized = normalize(colVal);

      const byTableName = (selectedColumnsData || []).find(
        (c) => `${c.tableName}.${c.name}` === normalized || normalize(`${c.tableName}.${c.name}`) === normalized
      );
      if (byTableName) {
        return `${byTableName.tableName}.${byTableName.name}`;
      }

      if (normalized.includes('.')) {
        return normalized;
      }
      return `${selectedTable ?? 'Unknown'}.${normalized}`;
    };

    const resolveChartColumn = (column: string) => {
      if (aggregateColumns && column) {
        const matchedAgg = aggregateColumns.find(
          (ac) =>
            ac.value === column ||
            ac.rawColumn === column ||
            ac.label === column ||
            (ac.rawColumn && column.includes(ac.rawColumn))
        );
        if (matchedAgg) {
          const meta = matchedAgg.rawColumn
            ? selectedColumnsData.find((c) => c.name === matchedAgg.rawColumn && c.tableName === selectedTable) ?? null
            : null;
          return { value: matchedAgg.value, meta };
        }
      }
      const meta = selectedColumnsData.find((col) => col.name === column && col.tableName === selectedTable) ?? null;
      return { value: column, meta };
    };

    const initTable = (cols: string[], limit: number | undefined) => {
      const resolved = cols.map((c: string) => resolveColumnValue(c));
      setColumns(resolved);
      setValue('visualizationType', { label: 'Table', value: 'Table' });
      setValue(
        'visualizationColumns',
        resolved.map((col: string) => ({ label: col, value: col }))
      );
      setRowLimit(limit);
      onChange({ type: 'Table', columns: resolved, rowLimit: limit });
    };

    const initChart = (visType: Exclude<VisualizationType, 'Table'>, column: string, tlType?: number) => {
      const { value: columnValue, meta } = resolveChartColumn(column);
      setSelectedColumnMeta(meta);
      if (tlType) {
        setTimelapseType(tlType);
      }

      setValue('visualizationType', { label: visType, value: visType });
      if (columnValue === '') {
        setValue('visualizationColumn', null);
      } else {
        setValue('visualizationColumn', { label: columnValue, value: columnValue });
      }
      if (tlType) {
        setValue('timelapseType', { label: 'Monthly', value: String(tlType) });
      }

      onChange({
        type: visType,
        column: columnValue,
        ...(tlType ? { timelapseType: tlType } : {}),
      });
    };

    if (initialVisualization) {
      const vis = initialVisualization;
      setType(vis.type ?? null);

      if (vis.type === 'Table') {
        const cols = vis.columns ?? [];
        initTable(cols, vis.rowLimit ?? rowLimit);
      } else {
        initChart(vis.type, vis.column ?? '', vis.timelapseType);
      }
    } else {
      setType(selectedVisualizationType ?? null);

      if (selectedVisualizationType === 'Table') {
        initTable(selectedVisualizationColumns ?? [], selectedVisualizationRowLimit ?? rowLimit);
      } else if (selectedVisualizationType) {
        initChart(selectedVisualizationType, selectedVisualizationColumn ?? '', selectedTimelapseType);
      }
    }

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800 py-5 flex items-center justify-between flex-col">
      <SelectFormInput
        name="visualizationType"
        control={control}
        options={VISUALIZATION_OPTIONS}
        label={t('Visualization')}
        labelClassName="mb-2 text-black-900 dark:text-white-00"
        placeholder={t('SelectType')}
        required
        onSelectValue={handleTypeChange}
        menuPlacement="top"
      />

      {type && type !== 'Table' && (
        <>
          <SelectFormInput
            className="mt-4"
            labelClassName="mb-2 text-black-900 dark:text-white-00"
            label={t('Column')}
            required
            name="visualizationColumn"
            control={control}
            options={availableColumns}
            placeholder={t('SelectColumn')}
            onSelectValue={handleSingleColumnChange}
            menuPlacement="top"
          />
          {selectedColumnMeta?.columnType === 5 && (
            <SelectFormInput
              className="mt-4"
              labelClassName="mb-2 text-black-900 dark:text-white-00"
              label={t('TimelapseType')}
              required
              name="timelapseType"
              control={control}
              options={FILTER_TIMELAPSE_TIME.map(({ value, label }) => ({ value: value.toString(), label }))}
              onSelectValue={handleTimelapseTypeChange}
              menuPlacement="top"
              value={timelapseType.toString()}
              defaultValue={DEFAULT_TIME_TYPE.value.toString()}
            />
          )}
        </>
      )}
      {type === 'Table' && (
        <>
          <div className="w-full mt-4">
            <label className="text-sm text-black-900 dark:text-white-00 mb-2 block">
              {t('Columns')}
              <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={columns}
              options={filteredGroupedOptions}
              onChange={(e: any) => {
                const values = e.value || [];
                setColumns(values);
                onChange({ type: 'Table', columns: values, rowLimit });
              }}
              optionLabel="label"
              optionValue="value"
              optionGroupLabel="label"
              optionGroupChildren="items"
              display="chip"
              filter
              placeholder={t('SelectColumns')}
              panelClassName="custom-multiselect-panel"
              className="w-full border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>
          <Input
            className="mt-4 w-full"
            labelClassName="mb-2 text-black-900 dark:text-white-00 text-sm"
            inputClassName="!text-sm"
            label={t('RowLimit')}
            name="rowLimit"
            type="number"
            value={rowLimit}
            onChange={handleRowLimitChange}
            placeholder="Row Limit"
          />
        </>
      )}
    </div>
  );
};

export default CreateReportTemplateVisualizationBlock;
